import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const { 
  FiAward, FiTrendingUp, FiGift, FiStar, FiCheckCircle, FiLock,
  FiShoppingCart, FiHistory, FiTarget, FiZap, FiCrown, FiShield,
  FiDiamond, FiClock, FiCalendar, FiFilter, FiSearch, FiBookOpen,
  FiUsers, FiVideo, FiUser, FiEdit3, FiMessageSquare
} = FiIcons;

const RewardsCenter = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');

  const userTier = user?.tier || 'bronze';
  const userPoints = user?.points || 0;

  const tiers = [
    {
      id: 'bronze',
      name: 'Bronze',
      icon: FiShield,
      color: 'orange',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      borderColor: 'border-orange-200',
      minPoints: 0,
      maxPoints: 999,
      benefits: [
        'Access to basic courses',
        'Community forum access',
        'Monthly newsletter',
        'Standard support'
      ]
    },
    {
      id: 'silver',
      name: 'Silver',
      icon: FiZap,
      color: 'gray',
      bgColor: 'bg-gray-50',
      textColor: 'text-gray-600',
      borderColor: 'border-gray-200',
      minPoints: 1000,
      maxPoints: 4999,
      benefits: [
        'All Bronze benefits',
        'Advanced course access',
        'Priority support',
        'Exclusive webinars',
        'Monthly CE credits'
      ]
    },
    {
      id: 'gold',
      name: 'Gold',
      icon: FiAward,
      color: 'yellow',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-600',
      borderColor: 'border-yellow-200',
      minPoints: 5000,
      maxPoints: 14999,
      benefits: [
        'All Silver benefits',
        'Premium course access',
        'VIP support',
        'Annual conference pass',
        'Mentorship program'
      ]
    },
    {
      id: 'platinum',
      name: 'Platinum',
      icon: FiCrown,
      color: 'purple',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      borderColor: 'border-purple-200',
      minPoints: 15000,
      maxPoints: Infinity,
      benefits: [
        'All Gold benefits',
        'Unlimited course access',
        'White-glove support',
        'Speaking opportunities',
        'Advisory board access'
      ]
    }
  ];

  const rewards = [
    {
      id: 1,
      title: 'Free CE Course',
      description: 'Unlock any premium continuing education course of your choice',
      points: 500,
      category: 'education',
      icon: FiBookOpen,
      available: true,
      popular: true,
      value: '$299'
    },
    {
      id: 2,
      title: 'Conference Ticket',
      description: 'Full access pass to the Annual DentalBridge Conference',
      points: 2000,
      category: 'events',
      icon: FiCalendar,
      available: userPoints >= 2000,
      popular: true,
      value: '$799'
    },
    {
      id: 3,
      title: 'Practice Consultation',
      description: '1-hour practice management consultation with industry experts',
      points: 1500,
      category: 'services',
      icon: FiUsers,
      available: userPoints >= 1500,
      popular: false,
      value: '$500'
    },
    {
      id: 4,
      title: 'Equipment Discount',
      description: '20% discount on selected dental equipment from partner vendors',
      points: 1000,
      category: 'discounts',
      icon: FiShoppingCart,
      available: userPoints >= 1000,
      popular: false,
      value: 'Up to $2,000'
    },
    {
      id: 5,
      title: 'Premium Webinar Series',
      description: 'Access to exclusive webinar series featuring top dental professionals',
      points: 300,
      category: 'education',
      icon: FiVideo,
      available: true,
      popular: false,
      value: '$199'
    },
    {
      id: 6,
      title: 'Mentorship Session',
      description: '30-minute mentorship session with a recognized dental expert',
      points: 800,
      category: 'services',
      icon: FiUser,
      available: userPoints >= 800,
      popular: true,
      value: '$250'
    }
  ];

  const achievements = [
    {
      id: 1,
      title: 'First Steps',
      description: 'Complete your first course',
      icon: FiTrendingUp,
      points: 100,
      unlocked: true,
      unlockedDate: '2024-01-10'
    },
    {
      id: 2,
      title: 'Knowledge Seeker',
      description: 'Complete 5 courses',
      icon: FiBookOpen,
      points: 250,
      unlocked: true,
      unlockedDate: '2024-01-08'
    },
    {
      id: 3,
      title: 'Community Leader',
      description: 'Make 50 helpful forum posts',
      icon: FiMessageSquare,
      points: 300,
      unlocked: false,
      progress: 32,
      total: 50
    },
    {
      id: 4,
      title: 'Referral Champion',
      description: 'Refer 10 new providers',
      icon: FiUsers,
      points: 500,
      unlocked: false,
      progress: 3,
      total: 10
    },
    {
      id: 5,
      title: 'Expert Contributor',
      description: 'Publish 5 approved articles',
      icon: FiEdit3,
      points: 400,
      unlocked: false,
      progress: 1,
      total: 5
    }
  ];

  const activityHistory = [
    {
      id: 1,
      type: 'earned',
      description: 'Completed course: Advanced Implantology',
      points: 100,
      date: '2024-01-15',
      category: 'education'
    },
    {
      id: 2,
      type: 'earned',
      description: 'Helpful forum post contribution',
      points: 25,
      date: '2024-01-14',
      category: 'community'
    },
    {
      id: 3,
      type: 'redeemed',
      description: 'Free CE Course redemption',
      points: -500,
      date: '2024-01-12',
      category: 'rewards'
    },
    {
      id: 4,
      type: 'earned',
      description: 'Achievement unlocked: Knowledge Seeker',
      points: 250,
      date: '2024-01-10',
      category: 'achievements'
    }
  ];

  const getTierInfo = (tierId) => {
    return tiers.find(tier => tier.id === tierId) || tiers[0];
  };

  const currentTier = getTierInfo(userTier);
  const nextTier = tiers[tiers.findIndex(t => t.id === userTier) + 1];

  const filteredRewards = rewards.filter(reward =>
    reward.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reward.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Rewards Center</h1>
              <p className="text-gray-600 mt-1">Earn points, unlock rewards, and advance your career.</p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm text-gray-600">Available Points</p>
                <p className="text-2xl font-bold text-gray-900">{userPoints.toLocaleString()}</p>
              </div>
              <div className={`px-4 py-2 rounded-lg ${currentTier.bgColor} ${currentTier.textColor} border ${currentTier.borderColor}`}>
                <SafeIcon icon={currentTier.icon} className="w-4 h-4 inline mr-2" />
                {currentTier.name} Tier
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tier Progress */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Your Tier Progress</h2>
            {nextTier && (
              <span className="text-sm text-gray-600">
                {nextTier.minPoints - userPoints} points to {nextTier.name}
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {tiers.map((tier, index) => (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className={`relative p-4 rounded-lg border-2 transition-all ${
                  userPoints >= tier.minPoints
                    ? `${tier.bgColor} ${tier.borderColor}`
                    : 'bg-gray-50 border-gray-200 opacity-60'
                }`}
              >
                <div className="text-center">
                  <SafeIcon icon={tier.icon} className={`w-8 h-8 mx-auto mb-2 ${tier.textColor}`} />
                  <h3 className={`font-semibold mb-2 ${tier.textColor}`}>{tier.name}</h3>
                  <p className="text-sm text-gray-600 mb-2">{tier.minPoints.toLocaleString()} points</p>
                  {userPoints >= tier.minPoints && (
                    <div className="flex items-center justify-center">
                      <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-green-500 mr-1" />
                      <span className="text-xs text-green-600">Active</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {nextTier && (
            <div className="mt-6">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-600">Progress to {nextTier.name}</span>
                <span className="font-medium text-gray-900">
                  {userPoints.toLocaleString()} / {nextTier.minPoints.toLocaleString()}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 h-3 rounded-full transition-all"
                  style={{ width: `${Math.min((userPoints / nextTier.minPoints) * 100, 100)}%` }}
                ></div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Navigation Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6"
        >
          <div className="flex space-x-8 p-6 border-b border-gray-100">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'rewards', label: 'Rewards Store', count: rewards.filter(r => r.available).length },
              { id: 'achievements', label: 'Achievements', count: achievements.filter(a => a.unlocked).length },
              { id: 'history', label: 'Activity' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  py-2 px-1 border-b-2 font-medium text-sm transition-colors
                  ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }
                `}
              >
                {tab.label}
                {tab.count !== undefined && (
                  <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Current Tier Benefits */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-100"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Tier Benefits</h3>
                <div className="space-y-3">
                  {currentTier.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500" />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
                {nextTier && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">Next Tier: {nextTier.name}</h4>
                    <p className="text-sm text-blue-700 mb-3">
                      Unlock {nextTier.benefits.length - currentTier.benefits.length} additional benefits
                    </p>
                    <button className="text-sm text-blue-600 font-medium hover:text-blue-700">
                      View all {nextTier.name} benefits →
                    </button>
                  </div>
                )}
              </motion.div>

              {/* Quick Stats */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Total Points Earned</span>
                    <span className="font-semibold text-gray-900">{userPoints.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Achievements</span>
                    <span className="font-semibold text-gray-900">
                      {achievements.filter(a => a.unlocked).length}/{achievements.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Rewards Redeemed</span>
                    <span className="font-semibold text-gray-900">3</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Member Since</span>
                    <span className="font-semibold text-gray-900">Jan 2024</span>
                  </div>
                </div>
              </motion.div>
            </div>
          )}

          {activeTab === 'rewards' && (
            <div>
              {/* Search and Filter */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                  <div className="relative">
                    <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search rewards..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
                    />
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-600">
                      {userPoints.toLocaleString()} points available
                    </span>
                    <button className="p-2 text-gray-400 hover:text-gray-600">
                      <SafeIcon icon={FiFilter} className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Rewards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredRewards.map((reward, index) => (
                  <motion.div
                    key={reward.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className={`bg-white rounded-xl shadow-sm border ${
                      reward.available 
                        ? 'border-gray-100 hover:shadow-md' 
                        : 'border-gray-100 opacity-60'
                    } transition-all`}
                  >
                    <div className="p-6">
                      {reward.popular && (
                        <div className="flex items-center justify-center mb-3">
                          <span className="px-3 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
                            Popular
                          </span>
                        </div>
                      )}
                      
                      <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                        <SafeIcon icon={reward.icon} className="w-6 h-6 text-blue-600" />
                      </div>
                      
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{reward.title}</h3>
                      <p className="text-gray-600 text-sm mb-4">{reward.description}</p>
                      
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-2xl font-bold text-gray-900">{reward.points.toLocaleString()}</span>
                        <span className="text-sm text-gray-500">points</span>
                      </div>
                      
                      <div className="text-sm text-gray-500 mb-4">
                        Value: {reward.value}
                      </div>
                      
                      <button
                        disabled={!reward.available}
                        className={`w-full py-2 rounded-lg font-medium transition-colors ${
                          reward.available
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {reward.available ? 'Redeem' : `${reward.points - userPoints} more points`}
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'achievements' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {achievements.map((achievement, index) => (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className={`bg-white rounded-xl p-6 shadow-sm border ${
                    achievement.unlocked 
                      ? 'border-green-200' 
                      : 'border-gray-200 opacity-75'
                  } transition-all`}
                >
                  <div className="text-center">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                      achievement.unlocked 
                        ? 'bg-green-100' 
                        : 'bg-gray-100'
                    }`}>
                      {achievement.unlocked ? (
                        <SafeIcon icon={achievement.icon} className="w-8 h-8 text-green-600" />
                      ) : (
                        <SafeIcon icon={FiLock} className="w-8 h-8 text-gray-400" />
                      )}
                    </div>
                    
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{achievement.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{achievement.description}</p>
                    
                    <div className="flex items-center justify-center space-x-2 mb-4">
                      <SafeIcon icon={FiAward} className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm font-medium text-gray-900">{achievement.points} points</span>
                    </div>
                    
                    {achievement.unlocked ? (
                      <div className="text-xs text-green-600">
                        Unlocked on {new Date(achievement.unlockedDate).toLocaleDateString()}
                      </div>
                    ) : (
                      <div>
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full transition-all"
                            style={{ width: `${(achievement.progress / achievement.total) * 100}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-500">
                          Progress: {achievement.progress}/{achievement.total}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {activeTab === 'history' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100"
            >
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Activity History</h3>
                <div className="space-y-4">
                  {activityHistory.map((activity, index) => (
                    <motion.div
                      key={activity.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          activity.type === 'earned' 
                            ? 'bg-green-100' 
                            : 'bg-red-100'
                        }`}>
                          <SafeIcon 
                            icon={activity.type === 'earned' ? FiTrendingUp : FiShoppingCart} 
                            className={`w-5 h-5 ${
                              activity.type === 'earned' ? 'text-green-600' : 'text-red-600'
                            }`} 
                          />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{activity.description}</p>
                          <p className="text-sm text-gray-500">
                            {new Date(activity.date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className={`text-lg font-semibold ${
                        activity.type === 'earned' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {activity.type === 'earned' ? '+' : ''}{activity.points}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RewardsCenter;